import React, { useState, useEffect } from 'react'
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  Eye, 
  Heart,
  Crown,
  Star,
  Coffee,
  Gift,
  BarChart3,
  Calendar,
  Download,
  Share2,
  Zap,
  Gem,
  Coins,
  Award,
  Target,
  Rocket,
  Shield,
  Lock,
  Globe,
  Activity,
  PieChart,
  LineChart
} from 'lucide-react'

interface Web3MonetizationStats {
  // Traditional metrics
  totalEarnings: number
  monthlyEarnings: number
  totalSubscribers: number
  totalTips: number
  totalPayPerView: number
  conversionRate: number
  
  // Web3 specific metrics
  nftSales: number
  tokenRewards: number
  daoParticipation: number
  crossChainEarnings: number
  defiYield: number
  governanceRewards: number
  
  // Blockchain metrics
  totalTransactions: number
  gasFeesPaid: number
  averageTransactionValue: number
  blockchainEfficiency: number
}

interface Web3MonetizationHubProps {
  creatorAddress: string
  stats: Web3MonetizationStats
  onRefresh: () => void
  isLoading?: boolean
}

export default function Web3MonetizationHub({ 
  creatorAddress, 
  stats, 
  onRefresh,
  isLoading = false 
}: Web3MonetizationHubProps) {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d')
  const [activeTab, setActiveTab] = useState<'overview' | 'web3' | 'analytics' | 'defi'>('overview')

  const formatCurrency = (amount: number) => `${amount.toFixed(2)} SUI`
  const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`

  const getRevenueStreamIcon = (type: string) => {
    switch (type) {
      case 'subscription': return <Crown className="w-4 h-4 text-purple-400" />
      case 'tip': return <Heart className="w-4 h-4 text-pink-400" />
      case 'payperview': return <Eye className="w-4 h-4 text-blue-400" />
      case 'nft': return <Gem className="w-4 h-4 text-violet-400" />
      case 'token': return <Coins className="w-4 h-4 text-yellow-400" />
      case 'defi': return <Zap className="w-4 h-4 text-green-400" />
      default: return <DollarSign className="w-4 h-4 text-gray-400" />
    }
  }

  const getRevenueStreamLabel = (type: string) => {
    switch (type) {
      case 'subscription': return 'Subscription'
      case 'tip': return 'Tips'
      case 'payperview': return 'Pay-per-view'
      case 'nft': return 'NFT Sales'
      case 'token': return 'Token Rewards'
      case 'defi': return 'DeFi Yield'
      default: return 'Other'
    }
  }

  const revenueStreams = [
    { type: 'subscription', amount: stats.totalSubscribers * 10, label: 'Subscriptions', color: 'purple' },
    { type: 'tip', amount: stats.totalTips, label: 'Tips', color: 'pink' },
    { type: 'payperview', amount: stats.totalPayPerView, label: 'Pay-per-view', color: 'blue' },
    { type: 'nft', amount: stats.nftSales, label: 'NFT Sales', color: 'violet' },
    { type: 'token', amount: stats.tokenRewards, label: 'Token Rewards', color: 'yellow' },
    { type: 'defi', amount: stats.defiYield, label: 'DeFi Yield', color: 'green' }
  ]

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{
           background: `
             linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
             radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
           `,
           backgroundSize: '100% 100%, 800px 800px, 900px 900px'
         }}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 bg-gold-400 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-40 right-20 w-1 h-1 bg-purple-400 rounded-full animate-pulse opacity-40" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-32 left-1/4 w-3 h-3 bg-violet-400 rounded-full animate-pulse opacity-30" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 right-1/3 w-2 h-2 bg-gold-400 rounded-full animate-pulse opacity-50" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-1 h-1 bg-blue-400 rounded-full animate-pulse opacity-30" style={{ animationDelay: '1.5s' }}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 
              className="text-4xl font-bold mb-2 bg-gradient-to-r from-gold-400 via-gold-500 to-violet-400 bg-clip-text text-transparent"
              style={{
                fontFamily: 'Georgia, serif',
                textShadow: `
                  1px 1px 0px rgba(0, 0, 0, 0.4),
                  -1px -1px 0px rgba(0, 0, 0, 0.4),
                  1px -1px 0px rgba(0, 0, 0, 0.4),
                  -1px 1px 0px rgba(0, 0, 0, 0.4),
                  0px 0px 4px rgba(0, 0, 0, 0.3)
                `
              }}
            >
              🚀 Web3 Monetization Hub
            </h1>
            <p className="text-purple-200 text-lg">
              Advanced creator economy powered by blockchain technology
            </p>
          </div>
        
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-gold-400 focus:border-transparent"
            >
              <option value="7d" className="bg-gray-800 text-white">Last 7 days</option>
              <option value="30d" className="bg-gray-800 text-white">Last 30 days</option>
              <option value="90d" className="bg-gray-800 text-white">Last 90 days</option>
              <option value="1y" className="bg-gray-800 text-white">Last year</option>
            </select>
            
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="px-6 py-2 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-black font-semibold rounded-lg transition-all duration-300 flex items-center shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: 'overview', label: '📊 Overview', icon: BarChart3 },
            { id: 'web3', label: '🌐 Web3 Revenue', icon: Globe },
            { id: 'analytics', label: '📈 Analytics', icon: LineChart },
            { id: 'defi', label: '⚡ DeFi & Yield', icon: Zap }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-gold-500 to-gold-600 text-black font-semibold shadow-lg'
                  : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Earnings */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <DollarSign className="w-6 h-6 text-green-400" />
                  </div>
                  <span className="text-sm text-green-400 font-medium">Total</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {formatCurrency(stats.totalEarnings)}
                </div>
                <div className="text-sm text-green-300">
                  All-time earnings
                </div>
                <div className="mt-2 text-xs text-green-400">
                  💎 Blockchain verified
                </div>
              </div>

              {/* Monthly Earnings */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <TrendingUp className="w-6 h-6 text-blue-400" />
                  </div>
                  <span className="text-sm text-blue-400 font-medium">This Month</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {formatCurrency(stats.monthlyEarnings)}
                </div>
                <div className="text-sm text-blue-300">
                  +12.5% from last month
                </div>
                <div className="mt-2 text-xs text-blue-400">
                  📈 Growth trend
                </div>
              </div>

              {/* Web3 Earnings */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <Gem className="w-6 h-6 text-violet-400" />
                  </div>
                  <span className="text-sm text-violet-400 font-medium">Web3</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {formatCurrency(stats.nftSales + stats.tokenRewards + stats.defiYield)}
                </div>
                <div className="text-sm text-violet-300">
                  NFT + Token + DeFi
                </div>
                <div className="mt-2 text-xs text-violet-400">
                  🚀 Next-gen revenue
                </div>
              </div>

              {/* Conversion Rate */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <Target className="w-6 h-6 text-orange-400" />
                  </div>
                  <span className="text-sm text-orange-400 font-medium">Conversion</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {formatPercentage(stats.conversionRate)}
                </div>
                <div className="text-sm text-orange-300">
                  View-to-purchase rate
                </div>
                <div className="mt-2 text-xs text-orange-400">
                  🎯 Performance
                </div>
              </div>
            </div>

            {/* Revenue Streams */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl p-6">
              <h2 
                className="text-2xl font-bold text-white mb-6"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                💰 Revenue Streams
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {revenueStreams.map((stream, index) => (
                  <div key={stream.type} className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-4 hover:bg-white/10 transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-white/10 to-white/5 rounded-lg group-hover:scale-110 transition-transform duration-300">
                          {getRevenueStreamIcon(stream.type)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-white">
                            {getRevenueStreamLabel(stream.type)}
                          </div>
                          <div className="text-xs text-purple-300">
                            {stream.label}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">
                          {formatCurrency(stream.amount)}
                        </div>
                        <div className="text-xs text-purple-400">
                          #{index + 1}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Web3 Tab */}
        {activeTab === 'web3' && (
          <div className="space-y-8">
            {/* Web3 Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* NFT Sales */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <Gem className="w-6 h-6 text-violet-400" />
                  </div>
                  <span className="text-sm text-violet-400 font-medium">NFT Sales</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {formatCurrency(stats.nftSales)}
                </div>
                <div className="text-sm text-violet-300">
                  Digital collectibles
                </div>
                <div className="mt-2 text-xs text-violet-400">
                  🎨 Art & collectibles
                </div>
              </div>

              {/* Token Rewards */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <Coins className="w-6 h-6 text-yellow-400" />
                  </div>
                  <span className="text-sm text-yellow-400 font-medium">Token Rewards</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {formatCurrency(stats.tokenRewards)}
                </div>
                <div className="text-sm text-yellow-300">
                  Community tokens
                </div>
                <div className="mt-2 text-xs text-yellow-400">
                  🪙 Creator tokens
                </div>
              </div>

              {/* DeFi Yield */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-xl hover:bg-white/15 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300">
                    <Zap className="w-6 h-6 text-green-400" />
                  </div>
                  <span className="text-sm text-green-400 font-medium">DeFi Yield</span>
                </div>
                <div className="text-3xl font-bold text-white mb-1">
                  {formatCurrency(stats.defiYield)}
                </div>
                <div className="text-sm text-green-300">
                  Liquidity rewards
                </div>
                <div className="mt-2 text-xs text-green-400">
                  ⚡ Yield farming
                </div>
              </div>
            </div>

            {/* Blockchain Analytics */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl p-6">
              <h2 
                className="text-2xl font-bold text-white mb-6"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                🔗 Blockchain Analytics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                  <Activity className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">
                    {stats.totalTransactions.toLocaleString()}
                  </div>
                  <div className="text-sm text-blue-300">Total Transactions</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                  <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">
                    {formatCurrency(stats.gasFeesPaid)}
                  </div>
                  <div className="text-sm text-green-300">Gas Fees Paid</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                  <Target className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">
                    {formatCurrency(stats.averageTransactionValue)}
                  </div>
                  <div className="text-sm text-purple-300">Avg Transaction</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                  <Rocket className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">
                    {formatPercentage(stats.blockchainEfficiency)}
                  </div>
                  <div className="text-sm text-orange-300">Efficiency</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl p-6">
              <h2 
                className="text-2xl font-bold text-white mb-6"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                📊 Advanced Analytics
              </h2>
              <div className="h-64 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <LineChart className="w-12 h-12 text-purple-400 mx-auto mb-2" />
                  <p className="text-purple-200">Advanced analytics dashboard</p>
                  <p className="text-sm text-purple-300">Coming soon with real-time data...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DeFi Tab */}
        {activeTab === 'defi' && (
          <div className="space-y-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl p-6">
              <h2 
                className="text-2xl font-bold text-white mb-6"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                ⚡ DeFi & Yield Farming
              </h2>
              <div className="h-64 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Zap className="w-12 h-12 text-green-400 mx-auto mb-2" />
                  <p className="text-green-200">DeFi integration coming soon</p>
                  <p className="text-sm text-green-300">Yield farming, liquidity pools, and more...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Panel */}
        <div className="mt-8 bg-gradient-to-r from-gold-500/20 to-purple-500/20 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
          <h3 
            className="text-2xl font-bold text-white text-center mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            🚀 Creator Economy Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="bg-gradient-to-r from-purple-500/20 to-purple-600/20 hover:from-purple-500/30 hover:to-purple-600/30 backdrop-blur-sm border border-purple-400/30 rounded-lg p-4 transition-all duration-300 group text-center">
              <Crown className="w-8 h-8 text-purple-400 mx-auto mb-2 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold">Manage Subscriptions</div>
              <div className="text-purple-300 text-sm">Configure tiers</div>
            </button>
            
            <button className="bg-gradient-to-r from-violet-500/20 to-violet-600/20 hover:from-violet-500/30 hover:to-violet-600/30 backdrop-blur-sm border border-violet-400/30 rounded-lg p-4 transition-all duration-300 group text-center">
              <Gem className="w-8 h-8 text-violet-400 mx-auto mb-2 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold">Mint NFTs</div>
              <div className="text-violet-300 text-sm">Create collectibles</div>
            </button>
            
            <button className="bg-gradient-to-r from-green-500/20 to-green-600/20 hover:from-green-500/30 hover:to-green-600/30 backdrop-blur-sm border border-green-400/30 rounded-lg p-4 transition-all duration-300 group text-center">
              <Zap className="w-8 h-8 text-green-400 mx-auto mb-2 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold">DeFi Integration</div>
              <div className="text-green-300 text-sm">Yield farming</div>
            </button>
            
            <button className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 hover:from-blue-500/30 hover:to-blue-600/30 backdrop-blur-sm border border-blue-400/30 rounded-lg p-4 transition-all duration-300 group text-center">
              <Download className="w-8 h-8 text-blue-400 mx-auto mb-2 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold">Export Data</div>
              <div className="text-blue-300 text-sm">Tax reporting</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
